import React, { useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { CharacteristicDataType } from "store/types";

import { currenciesArray } from "../../../../constants";
import { currencyCharVarName, MultipleDataItem } from "./MultipleDataItem";
import { DataItem, UpdateItem } from "./SingeCharForm";

interface Props {
  state: DataItem[];
  characteristicName: string;
  data_type: CharacteristicDataType;
  addItem: () => void;
  updateItem: UpdateItem;
  handleSplitPercentage: () => void;
  removeItem: (id: DataItem["id"]) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const MultipleData: React.FC<Props> = ({
  state,
  characteristicName,
  data_type,
  addItem,
  updateItem,
  handleSplitPercentage,
  removeItem,
  onSubmit,
  onCancel
}) => {
  const isCurrency = characteristicName === currencyCharVarName;

  const totalPercentage: number = useMemo(() => {
    return state.reduce((acc, item) => (item.is_deleted ? acc : (acc += item.percentage)), 0);
  }, [state]);

  // This currencies should be excluded from the select options to avoid currency duplication
  const filterCurrenciesOptions = (selectedCurr: string | null): string[] => {
    const selectedCurrencies: string[] = [];

    // iterate the data items, put all currencies except selected one
    // value is because of only specific characteristic as "Currency"
    state.forEach(({ value }) => {
      if (!value) return;
      if (selectedCurr && value === selectedCurr) return;
      selectedCurrencies.push(value);
    });

    return currenciesArray.filter((cur) => !selectedCurrencies.includes(cur));
  };

  const isDisabled = useMemo(() => {
    // Button is disabled when:
    // 1. Total percentage is not 0 or 100
    // 2. There are non-deleted items with empty values
    // 3. If there are any non-deleted items, total percentage must be 100%
    const hasInvalidPercentage = totalPercentage !== 0 && totalPercentage !== 100;
    const hasEmptyValue = state.some((item) => !item.is_deleted && !item.value);
    const allItemsDeleted = state.every((item) => item.is_deleted);
    const hasItemsButInvalidTotal = !allItemsDeleted && totalPercentage !== 100;

    return hasInvalidPercentage || (!allItemsDeleted && hasEmptyValue) || hasItemsButInvalidTotal;
  }, [state, totalPercentage]);

  return (
    <>
      <Stack sx={{ gap: "16px", overflow: "hidden" }}>
        <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
          <Typography
            sx={{
              fontSize: "16px",
              color: "#496682",
              lineHeight: "24px",
              fontWeight: 400
            }}
          >
            Total percentages should add up to 100%
          </Typography>
          <Button size="sm" sx={{ bgcolor: "#f5f6f7" }} onClick={handleSplitPercentage}>
            Spread Equally
          </Button>
        </Stack>
        <Stack sx={{ gap: "16px", overflow: "auto", py: "5px" }}>
          {state
            .filter((item) => !item.is_deleted)
            .map((item, _, array) => (
              <MultipleDataItem
                key={item.id}
                data={item}
                characteristicName={characteristicName}
                data_type={data_type}
                {...(!(isCurrency && array.length <= 1) && { onRemove: () => removeItem(item.id) })}
                onUpdate={(params) => updateItem({ id: item.id, ...params })}
                filterCurrenciesOptions={() => filterCurrenciesOptions(item.value)} // "Currency" characteristic has "value" field as main field
              />
            ))}
        </Stack>
        <IconButton onClick={addItem}>
          <CloseIcon sx={{ transform: "rotate(45deg)", bgcolor: "#f5f6f7" }} />
        </IconButton>
      </Stack>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "15px", lineHeight: "20px", color: "#496682" }}>
          Total: {totalPercentage}%
        </Typography>
        <Stack sx={{ flexDirection: "row", gap: "8px" }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" disabled={isDisabled} onClick={onSubmit}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
